/**
 * The Petty Cash submission form (docs/phases/petty-cash-consolidation.md,
 * Part 2). Fields mirror `PettyCashEntryIn` exactly: Transaction Date,
 * Purpose, an Entry Type select (expense/replenishment - Ruling 1's sign
 * lives here, not in the Total field), Total (`amount`, always positive),
 * and Petty Cash Balance (`reported_balance`, what the manager believes the
 * balance is after this entry - the server independently computes
 * `expected_balance` and flags a mismatch, shown back on success). No
 * `submitted_by`, bound server-side to the signed-in principal.
 *
 * Photos are upload-first through the shared `AttachmentSlots`, on Petty
 * Cash's own endpoint, 1-2 required (a single receipt shape, per the
 * consolidation doc - tighter than Cash Drop's 3).
 */
import { useState, type FormEvent } from "react";

import {
  ENTRY_TYPES,
  ENTRY_TYPE_LABELS,
  submitPettyCash,
  uploadPettyCashAttachment,
  type EntryType,
  type PettyCashRecord,
} from "../api/public";
import type { TokenGetter } from "../auth/session";
import { AttachmentSlots, errorMessage, useAttachmentSlots } from "../components/AttachmentSlots";
import { SessionGate } from "../components/SessionGate";

const MAX_SLOTS = 2;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Same normalization as the other two forms: fold a comma decimal separator
 * to a period and add a leading zero to a bare decimal (`.01`), before both
 * the validity check and the value sent. */
function normalizeMoney(value: string): string {
  const folded = value.trim().replace(",", ".");
  return folded.startsWith(".") ? `0${folded}` : folded;
}

function isValidMoney(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(normalizeMoney(value));
}

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; record: PettyCashRecord }
  | { status: "error"; message: string };

export function SubmitPettyCash() {
  return <SessionGate>{(getToken) => <SubmitPettyCashForm getToken={getToken} />}</SessionGate>;
}

function SubmitPettyCashForm({ getToken }: { getToken: TokenGetter }) {
  const [transactionDate, setTransactionDate] = useState(todayIso());
  const [purpose, setPurpose] = useState("");
  const [entryType, setEntryType] = useState<EntryType>("expense");
  const [amount, setAmount] = useState("");
  const [reportedBalance, setReportedBalance] = useState("");
  const uploads = useAttachmentSlots(uploadPettyCashAttachment, getToken, { maxSlots: MAX_SLOTS });
  const [submission, setSubmission] = useState<SubmitState>({ status: "idle" });

  const { attachmentIds, anyUploading } = uploads;

  // Submit is never hard-disabled on field validity - same reasoning as Cash
  // Drop's and Safe Count's forms (see `SubmitCashDrop.tsx`'s header).
  function validationErrors(): string[] {
    const errors: string[] = [];
    if (transactionDate.trim() === "") errors.push("Enter the transaction date.");
    if (purpose.trim() === "") errors.push("Enter the purpose.");
    if (!isValidMoney(amount)) errors.push("Enter a valid Total amount.");
    if (!isValidMoney(reportedBalance)) errors.push("Enter a valid Petty Cash Balance.");
    if (attachmentIds.length < 1) errors.push("Attach at least one photo.");
    if (attachmentIds.length > MAX_SLOTS) errors.push(`Attach at most ${MAX_SLOTS} photos.`);
    if (anyUploading) errors.push("Wait for the photo upload to finish.");
    return errors;
  }

  function resetForm() {
    setTransactionDate(todayIso());
    setPurpose("");
    setEntryType("expense");
    setAmount("");
    setReportedBalance("");
    uploads.reset();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submission.status === "submitting") return; // guards against a double tap
    const errors = validationErrors();
    if (errors.length > 0) {
      setSubmission({ status: "error", message: errors.join(" ") });
      return;
    }
    setSubmission({ status: "submitting" });
    try {
      const token = await getToken();
      const record = await submitPettyCash(
        {
          transaction_date: transactionDate,
          purpose,
          entry_type: entryType,
          amount: normalizeMoney(amount),
          reported_balance: normalizeMoney(reportedBalance),
          attachment_ids: attachmentIds,
        },
        token,
      );
      setSubmission({ status: "success", record });
      resetForm();
    } catch (error) {
      setSubmission({ status: "error", message: errorMessage(error) });
    }
  }

  return (
    <>
      {submission.status === "success" && (
        <section className="card success" data-testid="submit-success">
          <h2>Submitted</h2>
          <dl className="facts">
            <dt>Transaction date</dt>
            <dd>{submission.record.transaction_date}</dd>
            <dt>Purpose</dt>
            <dd>{submission.record.purpose}</dd>
            <dt>Entry type</dt>
            <dd>{ENTRY_TYPE_LABELS[submission.record.entry_type]}</dd>
            <dt>Total</dt>
            <dd>{submission.record.amount}</dd>
            <dt>Expected balance</dt>
            <dd>{submission.record.expected_balance}</dd>
            {submission.record.balance_discrepancy && (
              <>
                <dt>Discrepancy</dt>
                <dd className="field-error">
                  Reported {submission.record.reported_balance}, expected{" "}
                  {submission.record.expected_balance} (off by {submission.record.discrepancy_amount})
                </dd>
              </>
            )}
          </dl>
        </section>
      )}

      {/* noValidate: our own `validationErrors()` is the one source of truth,
          same reasoning as the other two forms' submit forms. */}
      <form className="card" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <h2>Petty Cash</h2>

        {submission.status === "error" && (
          <p className="field-error" role="alert" data-testid="submit-error">
            {submission.message}
          </p>
        )}

        <label className="field">
          Transaction date
          <input
            type="date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            required
          />
        </label>

        <label className="field">
          Purpose
          <input
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            required
          />
        </label>

        <label className="field">
          Entry type
          <select value={entryType} onChange={(e) => setEntryType(e.target.value as EntryType)} required>
            {ENTRY_TYPES.map((type) => (
              <option key={type} value={type}>
                {ENTRY_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          Total
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>

        <label className="field">
          Petty Cash Balance
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={reportedBalance}
            onChange={(e) => setReportedBalance(e.target.value)}
            required
          />
        </label>

        <AttachmentSlots
          legend="Photos (1-2 required)"
          slots={uploads.slots}
          onChange={uploads.handleSlotInputChange}
          onClear={uploads.clearSlot}
        />

        <button type="submit" disabled={submission.status === "submitting"}>
          {submission.status === "submitting" ? "Submitting…" : "Submit"}
        </button>
      </form>
    </>
  );
}
